<!-- @ts-nocheck -->
<template>
  <div class="ISTdateInput">
    <istforminput
      :readonly="readonly"
      :label="label"
      :ref="refinput"
      :id="refinput"
      :modelValue="inputStringDate"
      :name="id"
      :dense="dense"
      :disable="localdisable"
      :disabled="localdisable"
      :error="error || localError"
      :error-message="errorMessage"
      :placeholder="datePlaceholder"
      clearable
      @update:modelValue="inputChanged"
      @focus="inputFocus"
      @blur="inputBlur"
      @keydown="keyup"
      :mask="dateMask"
      class="ist-datepicker"
      autocomplete="off"
    />
    <q-menu
      :ref="refinputpop"
      v-model="showmenu"
      fit
      no-focus
      :persistent="isPersistent"
      :auto-close="false"
      :separate-close-popup="true"
      anchor="bottom middle"
      self="top middle"
      class="ist-datepicker_popup"
      @before-hide="menuBeforeHide"
      @blur="onMenuBlur"
    >
      <flat-pickr
        :ref="refFlatpickr"
        :modelValue="localFlatpickrDate"
        :config="dateConfig"
        :events="events"
        @on-change="flatpickrValueChanged"
        @on-day-create="onDayCreate"
        v-if="!localdisable"
      />
      <div class="multidate-wrapper" v-if="showMultiDates() && !isMobile()">
        <dtp-chip
          v-for="(item, index) in localValue"
          v-bind:key="index"
          :removable="canberemoved()"
          @remove="chipremovedate(item)"
          @click="chipGotoDate(item)"
          color="primary"
          text-color="white"
          icon="date"
          :label="getDateLabel(item)"
        />
      </div>
      <div class="popuptoolbar" v-if="showMultiDates()">
        <dtpBtn
          v-if="!disable"
          id="datepicker-clear-btn"
          color="secondary"
          style="float: left"
          @click="clearDates"
        >
          {{ $t("IstDatePicker.clearDates") }}
        </dtpBtn>
        <dtpBtn
          id="datepicker-close-btn"
          color="primary"
          style="float: right"
          @click="hideCal"
        >
          {{ $t("IstDatePicker.close") }}
        </dtpBtn>
      </div>
    </q-menu>
    <dtpBadge color="accent" floating v-if="showMultiDates()">
      {{
        getDatesCount() === 1
          ? $t("IstDatePicker.onDateSelected")
          : $t("IstDatePicker.datesChoosen", {
            datesCount: getDatesCount().toString(),
          })
      }}
    </dtpBadge>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck
import { ref, nextTick, watch, onMounted } from "vue";
import i18n from "@/i18n";
import istforminput from "./IstFormInput.vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { getDateFormat, getDateFormatRegex } from "../utils/dateUtils";
import { getFlatpickrLocale } from "../utils/flatpickrLocale";
import { getCurrentCountryConfig } from "@/countries";
import {
  isSameDay,
  isDate,
  format,
  isValid,
  addDays,
  addMonths,
  addYears,
  parseISO,
  min as mindate,
} from "date-fns";
import filters from "./filters/Filters";

defineOptions({
  name: "IstDatepicker",
  components: { flatPickr, istforminput },
});

const dateSeparator = () => getCurrentCountryConfig().dateSeparator;
const dateMask = `##${dateSeparator()}##${dateSeparator()}####`;
const datePlaceholder = `dd${dateSeparator()}mm${dateSeparator()}åååå`;

const props = defineProps({
  id: { type: String, required: true },
  modelValue: { default: null },
  min: { type: Date },
  max: { type: Date },
  disable: { type: Boolean },
  label: { type: String },
  multiple: { type: [Boolean, String], default: false },
  OnlyAllowEditOnFirstDate: { type: Boolean, default: false },
  dense: { type: [Boolean, String], default: false },
  error: { type: Boolean, default: false },
  errorMessage: { type: String },
});
const emit = defineEmits([
  "onDayCreated",
  "onErrorChange",
  "change",
  "input",
  "update:modelValue",
]);

const inputStringDate = ref("");
const localValue = ref<Date | Date[] | null>(null);
const localdisable = ref(false);
const localError = ref(false);
const refinput = ref<any[]>([]);
const refFlatpickr = ref<string>(props.id + "Flatpickr");
const refinputpop = ref<string>(props.id + "inputpop");
const localFlatpickrDate = ref<string | Date | Date[] | null>(null);
const isPersistent = ref(false);
const showmenu = ref(false);
const readonly = ref(false);
const events = ["onChange", "onDayCreate"];

const dateConfig = ref({
  locale: getFlatpickrLocale(),
  inline: true,
  weekNumbers: true,
  allowInput: false,
  mode: "single",
  clickOpens: false,
  static: true,
  appendTo: refinput.value[0],
});

onMounted(async () => {
  if (props.disable) localdisable.value = props.disable;
  if (props.multiple) {
    dateConfig.value.mode = "multiple";
    readonly.value = true;
    isPersistent.value = true;
  }
  if (Array.isArray(props.modelValue)) {
    localFlatpickrDate.value = [...props.modelValue];
  } else {
    onValueChanged(props.modelValue as any);
  }
});

watch(
  () => localError.value,
  () => {
    emit("onErrorChange", localError.value);
  },
);

watch(
  () => i18n.global.locale.value,
  () => {
    dateConfig.value = { ...dateConfig.value, locale: getFlatpickrLocale() };
  },
);

const Flatpickr = () =>
  (refinput.value[refFlatpickr.value] as any as flatPickr)?.fp;

const isMobile = () => {
  return window.innerWidth < 545;
};

async function onDayCreate(dObj, dStr, fp, calendarElement) {
  emit("onDayCreated", calendarElement);
}

function flatpickrValueChanged(selectedDates: Date[]) {
  if (selectedDates.length > 0) {
    if (props.multiple === true) {
      if (localValue.value !== null && !Array.isArray(localValue.value)) {
        // flatpickr returns strings !
        const s = (localValue.value as any).split(",");
        localValue.value = [];
        for (const element of s) {
          (localValue.value as Date[]).push(parseISO(element as string));
        }
      }
      if (!localValue.value) {
        localValue.value = [];
      }
      if (props.OnlyAllowEditOnFirstDate) {
        if ((localValue.value as Date[]).length > 0) {
          const currentDates: Date[] = [...(localValue.value as Date[])];
          const changedDates = selectedDates.filter(
            (newdate) =>
              currentDates.find((olddate) => isSameDay(olddate, newdate)) ==
              undefined,
          );
          if (changedDates.length == 1) {
            currentDates[0] = changedDates[0];
            localValue.value = [...currentDates];
            nextTick(() => {
              localFlatpickrDate.value = [...currentDates];
            });
          } else if (
            (localValue.value as Date[]).length !== selectedDates.length
          ) {
            localFlatpickrDate.value = [...(localValue.value as Date[])];
          }
        }
      } else {
        (localValue.value as Date[]).length = selectedDates.length;
        for (let index = 0; index < selectedDates.length; index++) {
          if (
            !isSameDay(
              (localValue.value as Date[])[index],
              selectedDates[index],
            )
          ) {
            (localValue.value as Date[])[index] = selectedDates[index];
          }
        }
      }
      sendUpdateValueEvent();
      writeDatesToInputField(localValue.value as Date[]);
    } else if (!Array.isArray(localValue.value)) {
      const dato: Date = selectedDates[selectedDates.length - 1] || new Date();
      if (!localValue.value || !isSameDay(localValue.value, dato)) {
        localError.value = false;
        localValue.value = dato;
        if (isDate(dato)) {
          writeDatesToInputField([dato]);
        }
        hideCal();
      }
    }
  } else {
    writeDatesToInputField([]);
    // Only reassign when the value really changes. Assigning a fresh [] on every
    // flatpickr change event retriggers the localValue watcher, which rewrites
    // localFlatpickrDate and makes flatpickr fire again - an endless loop that
    // froze the UI when clearing a multi-date selection.
    if (Array.isArray(props.modelValue)) {
      if (!Array.isArray(localValue.value) || localValue.value.length > 0) {
        localValue.value = [];
      }
    } else if (localValue.value !== null) {
      localValue.value = null;
    }
  }
}

function clearDates() {
  localValue.value = [];
}

function writeDatesToInputField(dates: Date[]) {
  if (dates.length > 0) {
    inputStringDate.value = format(mindate(dates), getDateFormat());
  } else {
    inputStringDate.value = "";
  }
}

// The wrapped q-input uses `unmasked-value`, so typed input arrives as digits
// only (e.g. "17062026"). Re-insert the separators so a typed date can
// be validated/committed the same way a calendar-picked one is. Without this a
// typed date is never written back to the model, and tabbing out of the field
// leaves the picker in an error state (SFOP-7297).
function normalizeDate(value: string | null): string | null {
  if (value == null) {
    return value;
  }
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 8) {
    // not a complete date yet (or already separated) — leave untouched
    return value;
  }
  const sep = dateSeparator();
  return `${digits.slice(0, 2)}${sep}${digits.slice(2, 4)}${sep}${digits.slice(4)}`;
}

// formInput @change="inputChanged"
function inputChanged(rawValue: string) {
  localError.value = false;
  const value = normalizeDate(rawValue);
  if (value && getDateFormatRegex().exec(value)) {
    // if the regex is hit, they are 'done' entering a date, and we can tell if its valid or not

    const validatedDate = validDateInput(value);
    if (validatedDate) {
      localError.value = false;
      localValue.value = validatedDate;
    } else {
      localError.value = true;
    }
    if (props.multiple) {
      writeDatesToInputField([]);
    }

    sendUpdateValueEvent();

    return;
  }
  if (value == null) {
    if (props.multiple) {
      localValue.value = [];
    } else {
      localValue.value = null;
    }
  } else {
    localError.value = true;
  }
  sendUpdateValueEvent();
}

async function keyup(event) {
  let AddValue = 0;
  // keyup
  if (event.keyCode == "38") {
    AddValue = 1;
  }
  // keydown
  if (event.keyCode == "40") {
    AddValue = -1;
  }
  const start = event.target.selectionStart;
  if (AddValue != 0) {
    event.stopPropagation();
    event.preventDefault();
    event.returnValue = false;
    event.cancelBubble = true;
    if (!Array.isArray(localValue.value)) {
      if (!localValue.value) {
        localValue.value = new Date();
      }
      if (event.target.selectionStart < 3) {
        localValue.value = addDays(localValue.value, AddValue);
      } else if (event.target.selectionStart < 6) {
        localValue.value = addMonths(localValue.value, AddValue);
      } else if (event.target.selectionStart < 11) {
        localValue.value = addYears(localValue.value, AddValue);
      }
    } else {
      if (event.target.selectionStart < 11) {
        if (localValue.value.length == 0) {
          localValue.value.push(new Date());
        }
        if (event.target.selectionStart < 3) {
          localValue.value[0] = addDays(localValue.value[0], AddValue);
        } else if (event.target.selectionStart < 6) {
          localValue.value[0] = addMonths(localValue.value[0], AddValue);
        } else if (event.target.selectionStart < 11) {
          localValue.value[0] = addYears(localValue.value[0], AddValue);
        }
        onLocalValueChanged(localValue.value, null);
      }
    }
    await nextTick();
    event.target.setSelectionRange(start, start);
    chipGotoDate(localValue.value[0]);
    return false;
  }
  return false;
}

watch(
  () => localValue.value,
  (val: Date | Date[] | null, valOld: Date | Date[] | null) => {
    onLocalValueChanged(val, valOld ?? null);
  },
);

function onLocalValueChanged(
  val: Date | Date[] | null,
  valOld: Date | Date[] | null = null,
) {
  if (JSON.stringify(val) !== JSON.stringify(valOld)) {
    localFlatpickrDate.value = val;
    if (val) {
      if (Array.isArray(val)) {
        writeDatesToInputField(val);
      } else {
        writeDatesToInputField([val]);
      }
    } else {
      writeDatesToInputField([]);
    }

    sendUpdateValueEvent();
  }
}
// form v-model changes
watch(
  () => props.modelValue,
  (val: Date | Date[] | undefined) => {
    onValueChanged(val);
  },
);

function onValueChanged(val: Date | Date[] | undefined) {
  if (val && Array.isArray(val)) {
    writeDatesToInputField(val);
  }
  if (JSON.stringify(val) !== JSON.stringify(localValue.value)) {
    if (val) {
      localValue.value = val;
    } else {
      localValue.value = null;
    }
    localError.value = false;
  }
}

function sendUpdateValueEvent() {
  emit("input", localValue.value);
  emit("change", localValue.value);
  emit("update:modelValue", localValue.value);
}

watch(
  () => props.disable,
  (val: boolean | undefined) => {
    if (val === true) {
      localdisable.value = val;
      hideCal();
      showmenu.value = false;
    } else {
      localdisable.value = false;
    }
  },
);

function getDatesCount() {
  if (Array.isArray(localValue.value)) {
    return localValue.value.length;
  } else {
    return 0;
  }
}

function showMultiDates() {
  return props.multiple;
}

function onMenuBlur() {
  if (!props.multiple) {
    hideCal();
  }
}

async function hideCal() {
  await nextTick();
  showmenu.value = false;
}

function inputFocus(event) {
  if (event) {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }
}

function inputBlur(event) {
  const value = normalizeDate(event.target.value);

  if (value == null || value == "" || validDateInput(value)) {
    return;
  }

  localError.value = true;
}

function menuBeforeHide(e) {
  if (e) {
    e.stopPropagation();
    e.stopImmediatePropagation();
  }
}

function validDateInput(value: string): Date | null {
  if (value !== "") {
    const res = getDateFormatRegex().exec(value);
    if (res) {
      const sep = dateSeparator();
      const dateArray = value.split(sep);
      const year = dateArray[2];
      const month = dateArray[1];
      const day = dateArray[0];
      const date = parseISO(`${year}-${month}-${day}`);
      if (isValid(date)) {
        return date;
      } else {
        localError.value = true;
      }
    } else {
      localError.value = true;
    }
  }
  return null;
}

function canberemoved() {
  if (localdisable.value || props.OnlyAllowEditOnFirstDate) return false;
  return true;
}
function chipremovedate(date: Date) {
  if (Array.isArray(localValue.value)) {
    localValue.value = localValue.value.filter((d) => !isSameDay(date, d));
  }
}
function chipGotoDate(date: Date) {
  const fp = Flatpickr();
  if (fp) fp.jumpToDate(date, false);
}
function getDateLabel(date: Date) {
  return filters.dateShortWithDayName(date);
}
</script>

<style scoped>
.hidden-picker {
  visibility: hidden;
  width: 0px;
  height: 0px;
  position: absolute;
}
.flatpickr-wrapper input {
  display: none;
}

@media screen and (max-width: 544px) {
  .ISTdateInput {
    position: relative;
    min-width: 135px;
  }
}

@media screen and (min-width: 545px) {
  .ISTdateInput {
    position: relative;
  }
}

.ISTdateInput :deep(input::-webkit-inner-spin-button),
.ISTdateInput :deep(input::-webkit-calendar-picker-indicator) {
  display: none;
  -webkit-appearance: none;
}

.multidate-wrapper {
  width: 200px;
  float: right;
  padding: 0 16px;
  overflow: auto;
  height: 345px;
}
.popuptoolbar {
  width: 100%;
  height: 40px;
  background-color: #eee;
  padding: 3px;
  display: table;
}
</style>
<style>
.grey-background {
  background-color: #eee;
  border-radius: 7px;
  min-width: 100%;
  min-height: 100%;
  position: absolute;
  z-index: -1;
  left: 0;
}

.event {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 150px;
  bottom: 3px;
  left: calc(50% - 1.5px);
  content: " ";
  display: block;
  background: #3d8eb9;
}

.event.trianglebottomright {
  background: none;
  width: 0;
  height: 0;
  border-radius: 0;
  border-bottom: 6px solid #f64747;
  border-left: 6px solid transparent;
}

.event.triangletopleft {
  background: none;
  width: 0;
  height: 0;
  border-radius: 0;
  border-top: 6px solid olivedrab;
  border-right: 6px solid transparent;
}
.event.circle {
  width: 6px;
  height: 6px;
  background: darkorange;
  border-radius: 50%;
}
.event.square {
  width: 6px;
  height: 6px;
  background: red;
}
.ist-datepicker_popup {
  z-index: 9000 !important;
  max-height: 345px !important;
}
.ist-datepicker_popup .flatpickr-calendar:before,
.ist-datepicker_popup .flatpickr-calendar:after {
  content: none;
}
.ist-datepicker_popup .flatpickr-calendar.inline {
  top: 0;
}
</style>
